import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { CSSProperties, ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useAuth } from '../auth/auth-context';
import { SentimentStrip, StripLegend } from '../components/sentiment';
import { SiteFooter } from '../components/site-footer';
import { SiteHeader } from '../components/site-header';
import { WatchlistToggle } from '../components/watchlist-toggle';
import {
  ApiError,
  fetchNews,
  fetchOverview,
  fetchTrend,
  type NewsResponse,
  type OverviewResponse,
  type TrendResponse,
} from '../lib/api';
import { formatScore, labelFor, toneFor } from '../lib/sentiment';
import { usePageTitle } from '../lib/page-title';

const TONE_CAPTION = {
  positive: 'Leaning positive',
  neutral: 'Mostly neutral',
  negative: 'Leaning negative',
} as const;

function formatDay(day: string, options: Intl.DateTimeFormatOptions = {}): string {
  // Days are calendar dates; format in UTC so the label never shifts a day.
  return new Date(`${day}T00:00:00Z`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
    ...options,
  });
}

function formatPublished(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
    timeZoneName: 'short',
  });
}

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

/**
 * Renders one data section's full state matrix. Every section is independent,
 * so a failing query degrades only its own block (partial page, not a blank
 * one), and unauthorized/error/empty are visibly distinct states.
 */
function Section<T>({
  title,
  hint,
  query,
  isEmpty,
  emptyMessage,
  children,
}: {
  title: string;
  hint?: string;
  query: UseQueryResult<T>;
  isEmpty: (data: T) => boolean;
  emptyMessage: string;
  children: (data: T) => ReactNode;
}) {
  const stale = query.isSuccess && !query.isFetching && query.isStale;

  let body: ReactNode;
  if (query.isPending) {
    body = (
      <div className="section-loading">
        <p role="status" className="visually-hidden">
          Loading…
        </p>
        <span className="bone" aria-hidden="true" />
        <span className="bone" aria-hidden="true" />
        <span className="bone" aria-hidden="true" />
      </div>
    );
  } else if (query.isError) {
    if (query.error instanceof ApiError && query.error.status === 401) {
      body = (
        <p className="section-state state-error" role="alert">
          <span>Your session has expired.</span>
          <Link className="text-link" to="/signin">
            Sign in again
          </Link>
        </p>
      );
    } else if (query.error instanceof ApiError && query.error.status === 403) {
      // A plan boundary, not a failure: the server derived a free-tier
      // entitlement and this ticker is outside the free catalog.
      body = (
        <p className="section-state state-pro" role="status">
          <span>This ticker is available on the Pro plan.</span>
          <Link className="text-link" to="/account">
            Upgrade from your account
          </Link>
        </p>
      );
    } else {
      body = (
        <p className="section-state state-error" role="alert">
          <span>Could not load {title.toLowerCase()}.</span>
          <button
            type="button"
            className="button-quiet button-inline"
            onClick={() => void query.refetch()}
          >
            Retry
          </button>
        </p>
      );
    }
  } else if (isEmpty(query.data)) {
    body = (
      <p className="section-state" role="status">
        {emptyMessage}
      </p>
    );
  } else {
    body = children(query.data);
  }

  return (
    <section className="ticker-section" aria-label={title}>
      <header className="ticker-section-header">
        <h2>{title}</h2>
        {hint && query.isSuccess ? <span className="hint">{hint}</span> : null}
        {query.isFetching && !query.isPending ? (
          <span className="hint" role="status">
            Refreshing…
          </span>
        ) : null}
        {stale ? <span className="stale-badge">Data may be stale</span> : null}
      </header>
      {body}
    </section>
  );
}

function Overview({ data }: { data: OverviewResponse }) {
  const latest = data.latest;
  if (!latest) return null;
  const tone = toneFor(latest.score);
  return (
    <div className="overview">
      <div className="overview-score">
        <span className={`figure tone-${tone}`} data-testid="overview-score">
          {formatScore(latest.score)}
        </span>
        <span className="caption">
          {TONE_CAPTION[tone]} on {formatDay(latest.day, { year: 'numeric' })}
        </span>
      </div>
      <div className="overview-detail">
        <SentimentStrip {...latest} large />
        <StripLegend {...latest} />
        <dl className="overview-grid">
          <div>
            <dt>Latest day</dt>
            <dd>{latest.day}</dd>
          </div>
          <div>
            <dt>Articles</dt>
            <dd>{latest.article_count}</dd>
          </div>
          <div>
            <dt>Model</dt>
            <dd title={data.model_revision ?? undefined}>{data.model_revision?.slice(0, 12)}</dd>
          </div>
        </dl>
      </div>
    </div>
  );
}

/** Scores cluster near zero, so scale to the data with a floor against noise. */
function trendScale(days: TrendResponse['days']): number {
  const peak = Math.max(...days.map((day) => Math.abs(day.score)));
  return Math.min(1, Math.max(0.25, Math.ceil(peak * 10) / 10));
}

function Trend({ data }: { data: TrendResponse }) {
  const scale = trendScale(data.days);
  const first = data.days[0];
  const last = data.days[data.days.length - 1];
  return (
    <div className="trend">
      <div className="trend-axis" aria-hidden="true">
        <span>{formatScore(scale)}</span>
        <span>0</span>
        <span>{formatScore(-scale)}</span>
      </div>
      <ol className="trend-list">
        {data.days.map((day) => {
          const direction = day.score >= 0 ? 'up' : 'down';
          const size = { '--size': `${(Math.abs(day.score) / scale) * 50}%` } as CSSProperties;
          return (
            <li key={day.day} tabIndex={0}>
              <span className={`trend-bar ${direction}`} style={size} />
              <span className="trend-tip">
                <span>{formatDay(day.day, { weekday: 'short' })}</span>
                <strong data-testid={`trend-${day.day}`}>{formatScore(day.score)}</strong>
                <span>{plural(day.article_count, 'article')}</span>
              </span>
            </li>
          );
        })}
      </ol>
      {first && last ? (
        <p className="trend-dates" aria-hidden="true">
          <span>{formatDay(first.day)}</span>
          {last !== first ? <span>{formatDay(last.day)}</span> : null}
        </p>
      ) : null}
    </div>
  );
}

function News({ data }: { data: NewsResponse }) {
  return (
    <ul className="news-list">
      {data.articles.map((article) => {
        const label = labelFor(article.positive, article.negative, article.neutral);
        return (
          <li key={article.url}>
            <div className="news-score">
              <span className={`sentiment-tag sentiment-${label}`}>
                {label} {formatScore(article.score)}
              </span>
              <SentimentStrip {...article} />
            </div>
            <div className="news-body">
              <a className="news-title" href={article.url} rel="noreferrer" target="_blank">
                {article.title}
              </a>
              {article.summary ? <p className="news-teaser">{article.summary}</p> : null}
              <p className="news-meta">
                <time dateTime={article.published_at}>{formatPublished(article.published_at)}</time>
              </p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export function TickerPage() {
  const { symbol = '' } = useParams();
  const { session } = useAuth();
  const token = session?.access_token ?? '';
  const sym = symbol.toUpperCase();
  usePageTitle(`${sym} sentiment`);

  const overview = useQuery<OverviewResponse>({
    queryKey: ['overview', sym, token],
    queryFn: () => fetchOverview(token, sym),
    enabled: Boolean(token && sym),
  });
  const trend = useQuery<TrendResponse>({
    queryKey: ['trend', sym, token],
    queryFn: () => fetchTrend(token, sym),
    enabled: Boolean(token && sym),
  });
  const news = useQuery<NewsResponse>({
    queryKey: ['news', sym, token],
    queryFn: () => fetchNews(token, sym),
    enabled: Boolean(token && sym),
  });

  return (
    <>
      <SiteHeader />
      <main className="page">
        <div className="ticker-head">
          <div>
            <nav className="crumbs" aria-label="Breadcrumb">
              <Link to="/tickers">Tickers</Link>
              <span aria-hidden="true">/</span>
              <span aria-current="page">{sym}</span>
            </nav>
            <h1>{sym}</h1>
            {overview.data?.name ? <p className="ticker-name">{overview.data.name}</p> : null}
          </div>
          <WatchlistToggle symbol={sym} />
        </div>

        <Section
          title="Overview"
          query={overview}
          isEmpty={(data) => data.latest === null}
          emptyMessage="No scored articles yet for this ticker."
        >
          {(data) => <Overview data={data} />}
        </Section>

        <Section
          title="Trend"
          hint="Daily score, last 30 days"
          query={trend}
          isEmpty={(data) => data.days.length === 0}
          emptyMessage="No daily history yet."
        >
          {(data) => <Trend data={data} />}
        </Section>

        <Section
          title="News"
          hint="Latest scored headlines"
          query={news}
          isEmpty={(data) => data.articles.length === 0}
          emptyMessage="No articles yet."
        >
          {(data) => <News data={data} />}
        </Section>
      </main>
      <SiteFooter />
    </>
  );
}
